export type ExperimentType = "discovery" | "validation" | "optimization";

export const experimentTypes = [
  {
    id: "discovery" as ExperimentType,
    label: "Discovery",
    description: "Explore unknowns and uncover insights",
    whenToUse: "EARLY-STAGE EXPLORATION, USER NEEDS RESEARCH",
    icon: "Compass",
  },
  {
    id: "validation" as ExperimentType,
    label: "Validation",
    description: "Test assumptions before building",
    whenToUse: "HYPOTHESIS TESTING, CONCEPT VALIDATION",
    icon: "CheckCircle",
  },
  {
    id: "optimization" as ExperimentType,
    label: "Optimization",
    description: "Improve existing experiences",
    whenToUse: "A/B TESTING, CONVERSION OPTIMIZATION",
    icon: "TrendingUp",
  },
];

export const contentByExperimentType: Record<
  ExperimentType,
  {
    objectivePlaceholder: string;
    objectiveSuggestion: string;
    coreQuestions: { placeholders: string[]; suggestions: string[] };
    primaryHypothesis: { placeholder: string; suggestion: string };
    keyAssumptions: { placeholders: string[]; suggestions: string[] };
    successSignals: {
      qualitative: { placeholder: string; suggestion: string };
      quantitative: { placeholder: string; suggestion: string };
    };
  }
> = {
  discovery: {
    objectivePlaceholder:
      "Understand unmet needs, pain points, and motivations in this workflow.",
    objectiveSuggestion:
      "Identify the top friction points and unmet expectations in the current card upgrade experience.",
    coreQuestions: {
      placeholders: [
        "What unknown user behavior are we trying to uncover?",
        "Where do users lose confidence in the flow?",
        "What emotional barriers reduce completion?",
      ],
      suggestions: [
        "Which step causes the highest confusion and why?",
        "What trust signals are users looking for before submitting?",
        "How does perceived effort impact drop-off intent?",
      ],
    },
    primaryHypothesis: {
      placeholder:
        "If we simplify the form language and sequence, then users will complete more steps, because they better understand what's required.",
      suggestion:
        "If we reduce jargon and simplify flow order, then users will progress further, because cognitive load and uncertainty will decrease.",
    },
    keyAssumptions: {
      placeholders: [
        "Users interpret eligibility criteria correctly",
        "Users trust data-sharing prompts",
        "Users can complete in one session",
      ],
      suggestions: [
        "Users perceive real-time verification as secure and reliable.",
        "Users prefer fewer steps even if each step has more content.",
        "Users understand the approval status indicators.",
      ],
    },
    successSignals: {
      qualitative: {
        placeholder: "Users report increased clarity and confidence in the flow.",
        suggestion:
          "Users describe the new flow as clearer and less intimidating during moderated sessions.",
      },
      quantitative: {
        placeholder: "Completion rate + time-to-complete + drop-off by step.",
        suggestion:
          "40% decrease in abandonment and 25% reduction in average completion time.",
      },
    },
  },
  validation: {
    objectivePlaceholder:
      "Validate whether proposed changes improve completion and reduce abandonment.",
    objectiveSuggestion:
      "Validate that a simplified 5-step credit card application flow reduces abandonment by 40% and increases completion rates compared to the current 12-step process.",
    coreQuestions: {
      placeholders: [
        "Does this change solve the intended user problem?",
        "What part of the hypothesis fails under real behavior?",
        "Are observed gains statistically meaningful?",
      ],
      suggestions: [
        "Will users trust the new auto-populated income verification from connected bank accounts?",
        "Does the simplified flow reduce cognitive load and time-to-completion as expected?",
        "Can users complete the new streamlined flow in under 5 minutes with fewer errors?",
      ],
    },
    primaryHypothesis: {
      placeholder:
        "If [change], then [outcome], because [reason].",
      suggestion:
        "If we reduce the credit card application steps from 12 to 5 with real-time document verification, then completion rates will increase by 40% and average completion time will drop below 5 minutes, because users abandon due to perceived complexity, excessive time investment, and uncertainty about approval status.",
    },
    keyAssumptions: {
      placeholders: [
        "State an assumption that must be true...",
        "State a second assumption...",
        "State a third assumption...",
      ],
      suggestions: [
        "Users will trust auto-populated income data from connected Chase accounts.",
        "Real-time document verification will not add confusion or delays.",
        "Users value fewer steps over seeing all details upfront.",
      ],
    },
    successSignals: {
      qualitative: {
        placeholder: "Positive feedback on simplicity, trust, and confidence.",
        suggestion:
          "Participants report the updated flow feels faster and easier to complete.",
      },
      quantitative: {
        placeholder: "Completion rate, abandonment, time-to-complete, error rate.",
        suggestion:
          "Completion improves by >= 40%, abandonment drops by >= 25%, and median completion time is <= 5 min.",
      },
    },
  },
  optimization: {
    objectivePlaceholder:
      "Optimize conversion and efficiency for an existing experience.",
    objectiveSuggestion:
      "Improve conversion and reduce friction in the existing upgrade journey through targeted UX optimizations.",
    coreQuestions: {
      placeholders: [
        "Which optimization has highest ROI?",
        "Where can friction be reduced safely?",
        "What changes drive lift without regressions?",
      ],
      suggestions: [
        "Which UI changes produce the strongest conversion lift?",
        "Which step-level interventions reduce abandonment most?",
        "What impact do microcopy changes have on completion speed?",
      ],
    },
    primaryHypothesis: {
      placeholder:
        "If we optimize high-friction touchpoints, then conversion will improve, because users can complete with less effort.",
      suggestion:
        "If we optimize the top two friction steps with clearer guidance and prefill, then conversion will increase and support contacts will decrease, because users encounter fewer blockers.",
    },
    keyAssumptions: {
      placeholders: ["Assumption 1", "Assumption 2", "Assumption 3"],
      suggestions: [
        "Users notice and use the optimization affordances.",
        "Changes do not increase compliance risk.",
        "Improvements generalize across user segments.",
      ],
    },
    successSignals: {
      qualitative: {
        placeholder: "Users describe flow as faster and less confusing.",
        suggestion:
          "Users report fewer pain points and greater confidence in completion.",
      },
      quantitative: {
        placeholder: "Conversion lift, drop-off reduction, support ticket reduction.",
        suggestion:
          "5-10% relative conversion lift with no increase in error rates.",
      },
    },
  },
};

export const autoPopulateByExperimentType = {
  discovery: {
    coreResearchQuestions: contentByExperimentType.discovery.coreQuestions.suggestions,
    primaryHypothesis: contentByExperimentType.discovery.primaryHypothesis.suggestion,
    keyAssumptions: contentByExperimentType.discovery.keyAssumptions.suggestions,
    successSignals: {
      qualitative: contentByExperimentType.discovery.successSignals.qualitative.suggestion,
      quantitative: contentByExperimentType.discovery.successSignals.quantitative.suggestion,
    },
  },
  validation: {
    coreResearchQuestions: contentByExperimentType.validation.coreQuestions.suggestions,
    primaryHypothesis: contentByExperimentType.validation.primaryHypothesis.suggestion,
    keyAssumptions: contentByExperimentType.validation.keyAssumptions.suggestions,
    successSignals: {
      qualitative: contentByExperimentType.validation.successSignals.qualitative.suggestion,
      quantitative: contentByExperimentType.validation.successSignals.quantitative.suggestion,
    },
  },
  optimization: {
    coreResearchQuestions: contentByExperimentType.optimization.coreQuestions.suggestions,
    primaryHypothesis: contentByExperimentType.optimization.primaryHypothesis.suggestion,
    keyAssumptions: contentByExperimentType.optimization.keyAssumptions.suggestions,
    successSignals: {
      qualitative: contentByExperimentType.optimization.successSignals.qualitative.suggestion,
      quantitative: contentByExperimentType.optimization.successSignals.quantitative.suggestion,
    },
  },
};
